import React, { useMemo, useEffect, useState, useCallback } from 'react'
import { MaterialReactTable } from 'material-react-table'
import { toast } from 'react-toastify'
import {
  Box,
  Checkbox,
  Button,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  TextField,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { Modal, Input, Select as AntSelect } from 'antd'
import * as XLSX from 'xlsx'
import {
  getProveedores,
  getRolUser,
  getConfigModelos,
  aplicarConsignaciones,
  actualizarEstadoModelo,
  actualizarCargabilidadModelo,
  actualizarDiasInventarioModelo,
  actualizarOrdenModelo,
  crearModeloPlaneacion,
  eliminarModeloPlaneacion,
  putInclusionLlegadasModelo,
} from '@auteco/services/motosSnopServices'
import { MRT_Localization_ES as MRTLocalizationES } from 'material-react-table/locales/es'
import './ConfiguracionModelosContainer.css'

const ConsignacionButton = styled(Button)(() => ({
  fontWeight: 'bold',
  marginLeft: '10px',
  backgroundColor: '#4a90e2',
  '&:hover': {
    backgroundColor: '#357abd',
  },
}))

const ExportButton = styled(Button)(() => ({
  fontWeight: 'bold',
  marginLeft: '10px',
  backgroundColor: '#28a745',
  '&:hover': {
    backgroundColor: '#218838',
  },
}))

const ConfiguracionModelosContainer = () => {
  const [infoBars, setInfoBars] = useState({})
  const [data, setData] = useState([])
  const [originalData, setOriginalData] = useState([]) // Guardar estado original
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  })
  const [editingCell, setEditingCell] = useState({ id: null, field: null })
  const [editValue, setEditValue] = useState('')
  const [showCrearModeloModal, setShowCrearModeloModal] = useState(false)
  const [isEditingMode, setIsEditingMode] = useState(false)
  const [dataForm, setDataForm] = useState({
    compania: '',
    marca: '',
    modelo: '',
    cargabilidad: '',
    diasInventarioEscogido: '',
    proveedor: '',
    fob: '',
    segmento: '',
  })
  const [proveedores, setProveedores] = useState([])
  const getInfoBars = async () => {
    const response = await getRolUser()
    if (response) {
      setInfoBars(response.iam.nombre)
    }
  }

  const handleGetProveedores = async () => {
    const response = await getProveedores()
    if (response.proveedores) {
      setProveedores(response.proveedores)
    }
  }

  const getFlagByCountry = (country) => {
    switch (country) {
      case 'CHINA':
        return '🇨🇳'
      case 'INDIA':
        return '🇮🇳'
      case 'MEXICO':
        return '🇲🇽'
      case 'USA':
        return '🇺🇸'
      case 'CANADA':
        return '🇨🇦'
      case 'BRAZIL':
        return '🇧🇷'
      case 'TAILANDIA':
        return '🇹🇭'
      case 'ESTADOS UNIDOS':
        return '🇺🇸'
      case 'JAPON':
        return '🇯🇵'
      case 'INDONESIA':
        return '🇮🇩'
      default:
        return ''
    }
  }

  const normalizeEstado = (estado) => {
    if (estado === null || estado === undefined) return ''

    // Normalizamos para que el Select pueda matchear por `value`
    // aunque el backend venga con mayúsculas o acentos (p.ej. "Evacuación").
    const stripAccents = (s) =>
      s.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

    const normalized = stripAccents(String(estado)).trim().toLowerCase()
    if (!normalized) return ''

    if (normalized.includes('activo')) return 'activo'
    if (normalized.includes('evacu')) return 'evacuacion'
    if (normalized.includes('inactivo')) return 'inactivo'
    if (normalized.includes('nuevo')) return 'nuevo'

    // Si viene un valor no contemplado, lo dejamos vacío para que el Select
    // muestre "Nuevo" (fallback en el render).
    return ''
  }

  useEffect(() => {
    handleGetProveedores()
  }, [])

  function handleInitializeData() {
    getInfoBars()
    const fetchData = async () => {
      const response = await getConfigModelos()
      if (response.configuracion_modelos) {
        const mappedData = response.configuracion_modelos.map((item) => ({
          id: item.id || `${item.marca}-${item.material}`, // Agregamos un ID único
          orden: item.orden || '',
          compania: item.compania || '',
          marca: item.marca || '',
          material: item.material || '',
          modelo: item.modelo_planeacion || '',
          segmento: item.segmento || '',
          proveedor: item.proveedor || '',
          origen: item.origen || '',
          cargabilidad: item.cargabilidad || '',
          leadtime: item.Leadtime ? `${item.Leadtime} días` : '',
          fob: item.fob || '',
          diasInventarioEscogido: item.dias_inventario_seleccionado || '',
          diasInventarioOptimo: item.dias_inventario_optimo || '',
          aplicaConsignacion:
            item.aplica_consignacion === '1' ||
            item.aplica_consignacion === 1 ||
            item.aplica_consignacion === true,
          inclusionLlegadas: item.inclusion_llegadas === '1' ||
            item.inclusion_llegadas === 1 ||
            item.inclusion_llegadas === true,
          estado: normalizeEstado(item.Estado ?? item.estado),
          modelo_creado: item.modelo_creado || '',
        }))
        setData(mappedData)
        setOriginalData(mappedData) // Guardar el estado original
      }
    }
    fetchData()
  }

  const handleInclusionLlegadasChange = async (modeloPlaneacion) => {
    const response = await putInclusionLlegadasModelo(modeloPlaneacion)
    if (response.success) {
      toast.success('Inclusión de llegadas actualizada exitosamente')
      handleInitializeData()
    } else {
      toast.error('Error al actualizar la inclusión de llegadas')
    }
  }

  const handleEliminarModelo = async (id) => {
    const response = await eliminarModeloPlaneacion(id)
    if (response.success) {
      toast.success('Modelo eliminado exitosamente')
    } else {
      toast.error('Error al eliminar el modelo')
    }
    handleInitializeData()
  }

  const handleCrearModeloPlaneacion = async () => {
    if (dataForm.proveedor === '') {
      toast.error('Seleccione la compañía')
      return
    }
    const proveedorData = proveedores.find(
      (proveedor) => proveedor.proveedor === dataForm.proveedor,
    )
    const requestBody = {
      compania: dataForm.compania,
      marca: dataForm.marca,
      modelo_planeacion: dataForm.modelo,
      cargabilidad: parseInt(dataForm.cargabilidad, 10),
      dias_inventario: parseInt(dataForm.diasInventarioEscogido, 10),
      proveedor: dataForm.proveedor,
      origen: proveedorData.origen,
      fob: parseInt(dataForm.fob, 10),
      segmento: dataForm.segmento,
    }

    const response = await crearModeloPlaneacion(requestBody)
    if (response.success) {
      setShowCrearModeloModal(false)
      setIsEditingMode(false)
      setDataForm({
        compania: '',
        marca: '',
        modelo: '',
        cargabilidad: '',
        diasInventarioEscogido: '',
        proveedor: '',
        fob: '',
        segmento: '',
      })
      toast.success(
        isEditingMode
          ? 'Modelo editado exitosamente'
          : 'Modelo creado exitosamente',
      )
      handleInitializeData()
    } else {
      toast.error(
        isEditingMode
          ? 'Error al editar el modelo'
          : 'Error al crear el modelo',
      )
    }
  }

  useEffect(() => {
    handleInitializeData()
  }, [])

  const handleConsignacionChange = useCallback((rowId, checked) => {
    setData((prevData) =>
      prevData.map((item) =>
        item.id === rowId ? { ...item, aplicaConsignacion: checked } : item,
      ),
    )
  }, [])

  const handleEstadoChange = useCallback(
    async (rowId, nuevoEstado) => {
      const item = data.find((d) => d.id === rowId)
      if (!item) return

      try {
        const result = await actualizarEstadoModelo(item.modelo, nuevoEstado)

        if (result.success) {
          // Actualizar el estado local solo si la petición fue exitosa
          setData((prevData) =>
            prevData.map((dataItem) =>
              dataItem.id === rowId
                ? { ...dataItem, estado: nuevoEstado }
                : dataItem,
            ),
          )

          // Actualizar también el estado original
          setOriginalData((prevData) =>
            prevData.map((dataItem) =>
              dataItem.id === rowId
                ? { ...dataItem, estado: nuevoEstado }
                : dataItem,
            ),
          )

          setSnackbar({
            open: true,
            message: result.message,
            severity: 'success',
          })
        } else {
          setSnackbar({
            open: true,
            message: result.message,
            severity: 'error',
          })
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Error al actualizar el estado del modelo',
          severity: 'error',
        })
      }
    },
    [data],
  )

  // Manejo de edición inline para cargabilidad y días inventario
  const handleEditStart = (rowId, field, value) => {
    setEditingCell({ id: rowId, field })
    setEditValue(value || '')
  }

  const handleEditCancel = () => {
    setEditingCell({ id: null, field: null })
    setEditValue('')
  }

  const handleEditSave = async (rowId, field) => {
    const item = data.find((d) => d.id === rowId)
    if (!item) return
    let result
    if (field === 'cargabilidad') {
      result = await actualizarCargabilidadModelo(
        item.modelo,
        Number(editValue),
      )
    } else if (field === 'diasInventarioEscogido') {
      result = await actualizarDiasInventarioModelo(
        item.modelo,
        Number(editValue),
      )
    } else if (field === 'orden') {
      result = await actualizarOrdenModelo(item.modelo, Number(editValue))
    }
    if (result && result.success) {
      setData((prevData) =>
        prevData.map((dataItem) =>
          dataItem.id === rowId
            ? { ...dataItem, [field]: editValue }
            : dataItem,
        ),
      )
      setOriginalData((prevData) =>
        prevData.map((dataItem) =>
          dataItem.id === rowId
            ? { ...dataItem, [field]: editValue }
            : dataItem,
        ),
      )
      setSnackbar({ open: true, message: result.message, severity: 'success' })
    } else {
      setSnackbar({
        open: true,
        message: result?.message || 'Error al guardar',
        severity: 'error',
      })
    }
    handleEditCancel()
  }

  const getElementosCambiados = () =>
    data.filter((item) => {
      const original = originalData.find((orig) => orig.id === item.id)
      return original && original.aplicaConsignacion !== item.aplicaConsignacion
    })

  const handleAplicarConsignaciones = async () => {
    const elementosCambiados = getElementosCambiados()

    if (elementosCambiados.length === 0) {
      setSnackbar({
        open: true,
        message: 'No hay cambios para aplicar.',
        severity: 'warning',
      })
      return
    }

    // Preparar los datos para enviar al endpoint
    const consignacionData = {
      modelo_planeacion: elementosCambiados
        .map((item) => item.modelo)
        .join('|'),
    }

    try {
      const response = await aplicarConsignaciones(consignacionData)
      if (response) {
        setSnackbar({
          open: true,
          message: `Se aplicaron ${elementosCambiados.length} cambios exitosamente.`,
          severity: 'success',
        })
        // Actualizar el estado original con los datos actuales después de aplicar exitosamente
        setOriginalData([...data])
      } else {
        setSnackbar({
          open: true,
          message: 'Error al aplicar los cambios. Intente nuevamente.',
          severity: 'error',
        })
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error al aplicar los cambios. Intente nuevamente.',
        severity: 'error',
      })
    }
  }

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  const handleExportExcel = () => {
    // Preparar los datos para exportar
    const exportData = data.map((item) => ({
      Orden: item.orden,
      Compañía: item.compania,
      Marca: item.marca,
      Material: item.material,
      Modelo: item.modelo,
      Segmento: item.segmento,
      Proveedor: item.proveedor,
      Origen: item.origen,
      Cargabilidad: item.cargabilidad,
      Leadtime: item.leadtime,
      FOB: item.fob,
      'Días Inv(Escogido)': item.diasInventarioEscogido,
      'Días Inv(Óptimo)': item.diasInventarioOptimo,
      Estado: item.estado,
      'Aplica Consignación': item.aplicaConsignacion ? 'Sí' : 'No',
      'Inclusión llegadas': item.inclusionLlegadas ? 'Sí' : 'No',
    }))

    // Crear el libro de trabajo
    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Configuración Modelos')

    // Generar el archivo y descargarlo
    const fileName = `configuracion_modelos_${
      new Date().toISOString().split('T')[0]
    }.xlsx`
    XLSX.writeFile(workbook, fileName)

    setSnackbar({
      open: true,
      message: 'Archivo Excel exportado exitosamente',
      severity: 'success',
    })
  }

  const handleCrearModelo = () => {
    setIsEditingMode(false)
    setDataForm({
      compania: '',
      marca: '',
      modelo: '',
      cargabilidad: '',
      diasInventarioEscogido: '',
      proveedor: '',
      fob: '',
      segmento: '',
    })
    setShowCrearModeloModal(true)
  }

  const handleEditarModelo = (row) => {
    setIsEditingMode(true)
    setDataForm({
      compania: row.compania,
      marca: row.marca,
      modelo: row.modelo,
      cargabilidad: row.cargabilidad,
      diasInventarioEscogido: row.diasInventarioEscogido,
      proveedor: row.proveedor,
      fob: row.fob,
      segmento: row.segmento,
    })
    setShowCrearModeloModal(true)
    handleInitializeData()
  }

  const columns = useMemo(
    () => [
      {
        accessorKey: 'orden',
        header: 'Orden',
        size: 100,
        Cell: ({ cell, row }) => {
          const isEditing =
            editingCell.id === row.original.id && editingCell.field === 'orden'
          if (isEditing) {
            return (
              <TextField
                value={editValue}
                onChange={(e) =>
                  setEditValue(e.target.value.replace(/[^0-9.]/g, ''))
                }
                size="small"
                type="number"
                autoFocus
                onBlur={() => handleEditSave(row.original.id, 'orden')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter')
                    handleEditSave(row.original.id, 'orden')
                  if (e.key === 'Escape') handleEditCancel()
                }}
                inputProps={{
                  min: 0,
                  style: { textAlign: 'center', width: 70 },
                }}
              />
            )
          }
          return (
            <Box
              sx={{ cursor: 'pointer' }}
              onClick={() =>
                handleEditStart(row.original.id, 'orden', cell.getValue())
              }
            >
              {row.original.orden || <span style={{ color: '#aaa' }}>-</span>}
            </Box>
          )
        },
      },
      {
        accessorKey: 'compania',
        header: 'Compañía',
        size: 100,
      },
      {
        accessorKey: 'marca',
        header: 'Marca',
        size: 100,
      },
      {
        accessorKey: 'material',
        header: 'Material',
        size: 100,
      },
      {
        accessorKey: 'modelo',
        header: 'Modelo',
        size: 150,
      },
      {
        accessorKey: 'segmento',
        header: 'Segmento',
        size: 120,
      },
      {
        accessorKey: 'proveedor',
        header: 'Proveedor',
        size: 150,
      },
      {
        accessorKey: 'origen',
        header: 'Origen',
        size: 100,
      },
      {
        accessorKey: 'cargabilidad',
        header: 'Cargabilidad',
        size: 120,
        Cell: ({ cell, row }) => {
          const isEditing =
            editingCell.id === row.original.id &&
            editingCell.field === 'cargabilidad'
          if (isEditing) {
            return (
              <TextField
                value={editValue}
                onChange={(e) =>
                  setEditValue(e.target.value.replace(/[^0-9.]/g, ''))
                }
                size="small"
                type="number"
                autoFocus
                onBlur={() => handleEditSave(row.original.id, 'cargabilidad')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter')
                    handleEditSave(row.original.id, 'cargabilidad')
                  if (e.key === 'Escape') handleEditCancel()
                }}
                inputProps={{
                  min: 0,
                  style: { textAlign: 'center', width: 70 },
                }}
              />
            )
          }
          return (
            <Box
              sx={{ cursor: 'pointer' }}
              onClick={() =>
                handleEditStart(
                  row.original.id,
                  'cargabilidad',
                  cell.getValue(),
                )
              }
            >
              {cell.getValue() || <span style={{ color: '#aaa' }}>-</span>}
            </Box>
          )
        },
      },
      {
        accessorKey: 'leadtime',
        header: 'Leadtime',
        size: 100,
      },
      {
        accessorKey: 'fob',
        header: 'FOB',
        size: 100,
      },
      // {
      //   accessorKey: 'diasInventarioOptimo',
      //   header: 'Días inv(Óptimo)',
      //   size: 150,
      // },
      {
        accessorKey: 'diasInventarioEscogido',
        header: 'Días Inv(Escogido)',
        size: 150,
        Cell: ({ cell, row }) => {
          const isEditing =
            editingCell.id === row.original.id &&
            editingCell.field === 'diasInventarioEscogido'
          if (isEditing) {
            return (
              <TextField
                value={editValue}
                onChange={(e) =>
                  setEditValue(e.target.value.replace(/[^0-9.]/g, ''))
                }
                size="small"
                type="number"
                autoFocus
                onBlur={() =>
                  handleEditSave(row.original.id, 'diasInventarioEscogido')
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter')
                    handleEditSave(row.original.id, 'diasInventarioEscogido')
                  if (e.key === 'Escape') handleEditCancel()
                }}
                inputProps={{
                  min: 0,
                  style: { textAlign: 'center', width: 70 },
                }}
              />
            )
          }
          return (
            <Box
              sx={{ cursor: 'pointer' }}
              onClick={() =>
                handleEditStart(
                  row.original.id,
                  'diasInventarioEscogido',
                  cell.getValue(),
                )
              }
            >
              {cell.getValue() || <span style={{ color: '#aaa' }}>Editar</span>}
            </Box>
          )
        },
      },
      {
        accessorKey: 'diasInventarioOptimo',
        header: 'Días Inv(Óptimo)',
        size: 150,
        Cell: ({ cell }) => <p>{parseFloat(cell.getValue()).toFixed(2)}</p>,
      },
      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 150,
        Cell: ({ cell, row }) => (
          <FormControl fullWidth size="small">
            <Select
              value={cell.getValue() || 'nuevo'}
              onChange={(e) =>
                handleEstadoChange(row.original.id, e.target.value)
              }
              sx={{
                minHeight: '32px',
                '& .MuiSelect-select': {
                  padding: '6px 12px',
                },
              }}
            >
              <MenuItem value="nuevo">Nuevo</MenuItem>
              <MenuItem value="activo">Activo</MenuItem>
              <MenuItem value="evacuacion">Evacuación</MenuItem>
              <MenuItem value="inactivo">Inactivo</MenuItem>
            </Select>
          </FormControl>
        ),
      },
      {
        accessorKey: 'aplicaConsignacion',
        header: 'Aplica consignación',
        size: 150,
        Cell: ({ cell, row }) => (
          <Checkbox
            checked={cell.getValue()}
            onChange={(e) =>
              handleConsignacionChange(row.original.id, e.target.checked)
            }
            sx={{
              color: '#666',
              '&.Mui-checked': {
                color: '#4a90e2',
              },
            }}
          />
        ),
      },
      {
        accessorKey: 'inclusionLlegadas',
        header: 'Inclusión llegadas',
        size: 150,
        Cell: ({ cell, row }) => (
          <Checkbox
            checked={cell.getValue()}
            onChange={() =>
              handleInclusionLlegadasChange(row.original.modelo)
            }
            sx={{
              color: '#666',
              '&.Mui-checked': {
                color: '#4a90e2',
              },
            }}
          />
        ),
      },  
      {
        accessorKey: 'modelo_creado',
        header: 'Acciones',
        size: 150,
        Cell: ({ row }) =>
          row.original.modelo_creado && (
            <Box sx={{ display: 'flex', gap: '8px' }}>
              <Button
                variant="contained"
                size="small"
                style={{
                  backgroundColor: '#4a90e21f',
                  border: 'solid #4a90e247',
                }}
                onClick={() => handleEditarModelo(row.original)}
              >
                ✏️
              </Button>
              <Button
                variant="contained"
                size="small"
                style={{
                  backgroundColor: '#ff11001f',
                  border: 'solid #ff000047',
                }}
                onClick={() => handleEliminarModelo(row.original.modelo)}
              >
                🗑️
              </Button>
            </Box>
          ),
      },
    ],
    [handleConsignacionChange, handleEstadoChange, editingCell, editValue],
  )

  const elementosCambiados = getElementosCambiados()

  if (infoBars !== 'Comprador') {
    return (
      <div>
        <p>No tienes permisos para acceder a esta página</p>
      </div>
    )
  }

  return (
    <>
      {showCrearModeloModal && (
        <Modal
          open={showCrearModeloModal}
          onCancel={() => {
            setShowCrearModeloModal(false)
            setIsEditingMode(false)
            setDataForm({
              compania: '',
              marca: '',
              modelo: '',
              cargabilidad: '',
              diasInventarioEscogido: '',
              proveedor: '',
              fob: '',
              segmento: '',
            })
          }}
          footer={null}
          width={600}
          centered
          closable
          styles={{
            body: { padding: 0 },
          }}
        >
          <div>
            {/* Título */}
            <div
              style={{
                color: 'black',
                padding: '25px',
                textAlign: 'center',
                fontSize: '18px',
                fontWeight: 'bold',
                letterSpacing: '1px',
              }}
            >
              {isEditingMode
                ? 'EDITAR MODELO DE PLANEACION'
                : 'CREAR MODELO DE PLANEACION'}
            </div>

            {/* Formulario */}
            <div style={{ padding: '20px' }}>
              {/* Compañía */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '25px',
                  gap: '30px',
                }}
              >
                <p className="config-modal-label">Compañía</p>
                <AntSelect
                  className="config-modal-select"
                  defaultValue="TVS"
                  value={dataForm.compania}
                  onChange={(value) =>
                    setDataForm({ ...dataForm, compania: value })
                  }
                  size="large"
                >
                  <AntSelect.Option value="TVS">TVS</AntSelect.Option>
                  <AntSelect.Option value="VICTORY+">VICTORY+</AntSelect.Option>
                  <AntSelect.Option value="ELECTRIC">ELECTRIC</AntSelect.Option>
                  <AntSelect.Option value="BLUE">BLUE</AntSelect.Option>
                </AntSelect>
              </div>

              {/* Marca */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '25px',
                  gap: '30px',
                }}
              >
                <p className="config-modal-label">Marca</p>
                <Input
                  className="config-modal-input"
                  value={dataForm.marca}
                  onChange={(e) =>
                    setDataForm({ ...dataForm, marca: e.target.value })
                  }
                  size="large"
                />
              </div>

              {/* Modelo */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '25px',
                  gap: '30px',
                }}
              >
                <p className="config-modal-label">Modelo</p>
                <Input
                  value={dataForm.modelo}
                  onChange={(e) =>
                    setDataForm({ ...dataForm, modelo: e.target.value })
                  }
                  size="large"
                  className="config-modal-input"
                  disabled={isEditingMode}
                />
              </div>

              {/* Cargabilidad */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '25px',
                  gap: '30px',
                }}
              >
                <p className="config-modal-label">Cargabilidad</p>
                <Input
                  type="number"
                  size="large"
                  value={dataForm.cargabilidad}
                  onChange={(e) =>
                    setDataForm({ ...dataForm, cargabilidad: e.target.value })
                  }
                  className="config-modal-input"
                />
              </div>

              {/* Días Inventario Escogido */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '25px',
                  gap: '30px',
                }}
              >
                <p className="config-modal-label">Días Inventario Escogido</p>
                <Input
                  type="number"
                  value={dataForm.diasInventarioEscogido}
                  onChange={(e) =>
                    setDataForm({
                      ...dataForm,
                      diasInventarioEscogido: e.target.value,
                    })
                  }
                  size="large"
                  className="config-modal-input"
                />
              </div>

              {/* Proveedor */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '25px',
                  gap: '30px',
                }}
              >
                <p className="config-modal-label">Proveedor</p>
                <AntSelect
                  value={dataForm.proveedor}
                  onChange={(value) =>
                    setDataForm({ ...dataForm, proveedor: value })
                  }
                  className="config-modal-select"
                  placeholder="Desplegable con los proveedores existentes"
                  size="large"
                >
                  {/* Opciones se cargarán dinámicamente */}
                  {proveedores.map((proveedor) => (
                    <AntSelect.Option
                      key={proveedor.proveedor}
                      value={proveedor.proveedor}
                    >
                      {getFlagByCountry(proveedor.origen)} {proveedor.proveedor}
                    </AntSelect.Option>
                  ))}
                </AntSelect>
              </div>

              {/* FOB unitario */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '35px',
                  gap: '30px',
                }}
              >
                <p className="config-modal-label">FOB unitario</p>
                <Input
                  className="config-modal-input"
                  type="number"
                  size="large"
                  value={dataForm.fob}
                  onChange={(e) =>
                    setDataForm({ ...dataForm, fob: e.target.value })
                  }
                />
              </div>

              {/* segmento  text input */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '25px',
                  gap: '30px',
                }}
              >
                <p className="config-modal-label">Segmento</p>
                <Input
                  value={dataForm.segmento}
                  onChange={(e) =>
                    setDataForm({ ...dataForm, segmento: e.target.value })
                  }
                  size="large"
                  className="config-modal-input"
                />
              </div>

              {/* Botón Aceptar */}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '40px',
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  disabled={
                    dataForm.compania === '' ||
                    dataForm.marca === '' ||
                    dataForm.modelo === '' ||
                    dataForm.cargabilidad === '' ||
                    dataForm.diasInventarioEscogido === '' ||
                    dataForm.proveedor === '' ||
                    dataForm.fob === ''
                  }
                  onClick={() => {
                    handleCrearModeloPlaneacion()
                  }}
                >
                  {isEditingMode ? 'EDITAR' : 'ACEPTAR'}
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
      <Box>
        <div style={{ padding: '40px' }}>
          <p style={{ fontSize: '24px', fontWeight: 'bold', color: 'black' }}>
            Configuración de Modelos
          </p>
          <div
            style={{
              width: '100%',
              height: '100%',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            <MaterialReactTable
              columns={columns}
              data={data}
              columnResizeMode="onEnd"
              muiTableContainerProps={{ sx: { maxHeight: '60vh' } }}
              enableColumnFilters
              enablePagination
              enableSorting
              initialState={{
                density: 'compact',
              }}
              muiTableHeadCellProps={{
                sx: {
                  backgroundColor: '#fafafa',
                },
              }}
              muiTableBodyCellProps={{
                sx: {
                  fontSize: '12px',
                },
              }}
              muiTableBodyRowProps={({ row }) => ({
                sx: {
                  backgroundColor: row.original.modelo_creado
                    ? '#e9e9e9'
                    : 'white',
                },
              })}
              muiTablePaginationProps={{
                sx: {
                  fontSize: '50px',
                },
              }}
              localization={MRTLocalizationES}
            />
          </div>

          <Box sx={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
            <ConsignacionButton
              variant="contained"
              size="large"
              onClick={handleAplicarConsignaciones}
              disabled={elementosCambiados.length === 0}
            >
              Aplica  consignación ({elementosCambiados.length})
            </ConsignacionButton>

            <ExportButton
              variant="contained"
              size="large"
              onClick={handleExportExcel}
              disabled={data.length === 0}
            >
              Exportar
            </ExportButton>

            <Button
              variant="contained"
              size="large"
              disabled={data.length === 0}
              style={{
                backgroundColor: '#4a90e2',
                color: 'white',
                marginLeft: 'auto',
              }}
              onClick={handleCrearModelo}
            >
              Crear modelo +
            </Button>
          </Box>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity={snackbar.severity}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </div>
      </Box>
    </>
  )
}

export default ConfiguracionModelosContainer
